// Ctrl/Meta+ホイールを「ブラウザ拡大縮小」ではなく「キャンバスのズーム」へ振り替える共通フック。
//
// キャンバス面（canvas-container）では use-wheel-pan-zoom が同等の処理を行うが、その上に浮かぶ
// ツールバー・ミニマップ・参加者一覧などの UI パネルは canvas-container の DOM 子孫ではないため、
// パネル上での Ctrl+ホイールはキャンバスのズーム listener に届かず、ブラウザのページ拡大縮小が
// 動いてしまう（要件⑧）。そこで各パネルのルート要素へ本フックを付け、キャンバス上と同じ挙動
// （ブラウザ拡大を抑止してキャンバスをズーム）に揃える。
//
// 設計: comment-layer.tsx が採用済みの「パネルごとに非 passive wheel listener を張る」方式を踏襲し、
// window / document 全体へ非 passive listener を張る方式は採らない（通常スクロールの応答性低下を避け、
// かつ canvas-container の既存 listener と二重処理しないため）。パネルはキャンバスの兄弟要素であり、
// wheel は兄弟subtree へはバブルしないので二重発火は起きない。
//
// Ctrl/Meta を伴わない通常ホイールでは何もしない（early return）ため、パネル内のスクロール可能な
// 領域（例: 省略参加者一覧のポップオーバー）の通常スクロールは阻害しない。

import { useEffect, type RefObject } from "react";
import { useCanvasStore } from "../store/canvas-store";
import { applyWheelZoom } from "../../../lib/canvas-coordinates";

export function useCtrlWheelZoom(ref: RefObject<HTMLElement | null>): void {
  const setView = useCanvasStore((s) => s.setView);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const onWheel = (event: WheelEvent): void => {
      // 通常ホイールはパネル内スクロール等に委ね、何もしない。
      if (!event.ctrlKey && !event.metaKey) return;
      // ブラウザのページ拡大縮小を抑止し、キャンバスのズームへ振り替える。
      event.preventDefault();
      const state = useCanvasStore.getState();
      // canvas-container 外での view 変更は自動 arm されないため、明示的に arm 要求して
      // 表示位置の保存 / URL 反映を有効化する（comment-layer と同じ順序）。
      state.requestViewSyncArm();
      // キャンバス本体と同じ振り分け（マウスホイール=段階ズーム / トラックパッドのピンチ=無段階ズーム）。
      setView(applyWheelZoom(state.view, { x: event.clientX, y: event.clientY }, event));
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [ref, setView]);
}
